import { ArrowRight, Check, ExternalLink } from 'lucide-react'
import { DiscordSupportLink } from '@/components/DiscordSupportLink'
import { SiteFooter } from '@/components/SiteFooter'
import { SiteHeader } from '@/components/SiteHeader'
import { ButtonLink, Collapsible } from '@/components/play'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import '@/pages/ChatGptGuidePage.css'

const chapters = [
  ['before-you-start', 'Before you start'],
  ['add-streambridge', 'Add StreamBridge'],
  ['link-your-account', 'Link your account'],
  ['open-your-workspace', 'Open Event workspace'],
  ['create-your-event', 'Create an event'],
  ['prepare-your-cameras', 'Prepare your cameras'],
  ['next-steps', 'Take it live'],
] as const

function Screenshot({ file, alt, caption, width, height }: {
  file: string; alt: string; caption: string; width: number; height: number
}) {
  const src = `/docs/images/chatgpt/${file}`
  return (
    <figure className="chatgpt-guide__figure">
      <a href={src} target="_blank" rel="noopener noreferrer" aria-label={`Enlarge screenshot: ${alt} (opens in a new tab)`}>
        <img src={src} alt={alt} width={width} height={height} loading="lazy" />
      </a>
      <figcaption><span>{caption}</span><a href={src} target="_blank" rel="noopener noreferrer">Enlarge <ExternalLink size={14} aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span></a></figcaption>
    </figure>
  )
}

/**
 * The ChatGPT guide (/docs/chatgpt): a `.play-site` page with the shared header and footer on the site gutter, an intro,
 * the chapters and the guide itself.
 */
export function ChatGptGuidePage() {
  useDocumentTitle('Set up an event in ChatGPT · StreamBridge')

  return (
    <div className="play-theme chatgpt-guide play-site">
      <a className="chatgpt-guide__skip" href="#guide-content">Skip to guide</a>
      <SiteHeader />

      <main>
        <div className="chatgpt-guide__intro">
          <p className="chatgpt-guide__eyebrow">Host guide <span aria-hidden="true">/</span> ChatGPT pilot</p>
          <h1>Your event.<br />Inside ChatGPT.</h1>
          <p className="chatgpt-guide__lead">Connect StreamBridge, create your endurance event, and get your cameras ready from a native workspace in ChatGPT.</p>
          <div className="chatgpt-guide__intro-actions">
            <ButtonLink href="#before-you-start">Follow the guide <ArrowRight aria-hidden="true" /></ButtonLink>
            <span>Preview draft · 3 October 2026</span>
          </div>
        </div>

        <div className="chatgpt-guide__layout">
          <nav className="chatgpt-guide__contents" aria-label="On this page">
            <p>In this guide</p>
            <ol>{chapters.map(([id, label], index) => <li key={id}><a href={`#${id}`}><span aria-hidden="true">{String(index).padStart(2, '0')}</span>{label}</a></li>)}</ol>
          </nav>

          <article id="guide-content" className="chatgpt-guide__article" tabIndex={-1}>
            <section id="before-you-start" aria-labelledby="before-title">
              <p className="chatgpt-guide__step">Before you start</p>
              <h2 id="before-title">A small setup. Then your event.</h2>
              <p>This guide covers the StreamBridge ChatGPT pilot. You’ll need:</p>
              <ul className="chatgpt-guide__checklist">
                <li><Check aria-hidden="true" /><span>A ChatGPT account or workspace that can add a custom MCP plugin.</span></li>
                <li><Check aria-hidden="true" /><span>A StreamBridge account with permission to manage events in your organization.</span></li>
                <li><Check aria-hidden="true" /><span>The current HTTPS connection URL from the StreamBridge team, ending in <code>/mcp/chatgpt</code>.</span></li>
              </ul>
              <aside className="chatgpt-guide__note">
                <strong>Pilot access</strong>
                <p>The native extension is being tested with invited hosts. Ask the team for an enabled organization and a connection URL. The temporary address shown in these screenshots is an example and may expire.</p>
              </aside>
            </section>

            <section id="add-streambridge" aria-labelledby="add-title">
              <p className="chatgpt-guide__step">01 / Connect</p>
              <h2 id="add-title">Add StreamBridge to ChatGPT.</h2>
              <p>Open <a href="https://chatgpt.com/plugins" target="_blank" rel="noopener noreferrer">Plugins in ChatGPT<span className="sr-only"> (opens in a new tab)</span></a>. Choose <strong>Add</strong>, then <strong>Create custom MCP server</strong>. The available controls depend on your ChatGPT account and workspace settings.</p>
              <Screenshot file="01-plugins-directory.jpg" alt="ChatGPT Plugins page with the Add button at the top right" caption="Start in ChatGPT’s Plugins page." width={960} height={450} />
              <p>Enter <strong>StreamBridge</strong> as the name, paste the connection URL from the team, and select <strong>OAuth</strong> for authentication.</p>
              <Screenshot file="02-connection-settings.jpg" alt="ChatGPT Create custom MCP server dialog with StreamBridge, an HTTPS server URL, and OAuth selected" caption="Use the complete server URL, including /mcp/chatgpt." width={607} height={790} />
              <Collapsible trigger="Check the OAuth settings" keepMounted>
                <p>ChatGPT discovers the authorization settings from the server. Open <strong>Advanced OAuth settings</strong> to check that the registration method is <strong>Dynamic Client Registration (DCR)</strong>. You don’t need to supply a client ID or client secret for this pilot.</p>
                <p>The six scopes cover your profile, reading and editing events, reading and editing cameras, and opening camera invitation handoffs:</p>
                <p className="chatgpt-guide__scopes"><code>profile_read</code><code>events_read</code><code>events_write</code><code>streams_read</code><code>streams_write</code><code>streams_invite</code></p>
                <Screenshot file="03-oauth-discovery.jpg" alt="ChatGPT advanced OAuth settings showing Dynamic Client Registration discovered for StreamBridge" caption="ChatGPT discovers the registration method and OAuth endpoints." width={964} height={747} />
              </Collapsible>
              <p>Review ChatGPT’s custom-server notice. If you want to connect, select <strong>I understand and want to continue</strong>, then <strong>Create as a plugin</strong>.</p>
            </section>

            <section id="link-your-account" aria-labelledby="link-title">
              <p className="chatgpt-guide__step">02 / Authorize</p>
              <h2 id="link-title">Choose your organization.</h2>
              <p>Follow the connection prompt to StreamBridge and sign in. Choose the organization you want ChatGPT to work with, review the requested permissions, and approve the connection.</p>
              <p>The connection belongs to that organization. Your existing StreamBridge role still determines what you can edit.</p>
            </section>

            <section id="open-your-workspace" aria-labelledby="workspace-title">
              <p className="chatgpt-guide__step">03 / Open</p>
              <h2 id="workspace-title">Open Event workspace.</h2>
              <p>Open StreamBridge’s <strong>Event workspace</strong> in ChatGPT. The native panel lets you find an existing event or create a new one in your connected organization.</p>
              <p>You can also ask ChatGPT to open it:</p>
              <blockquote>Open my StreamBridge event workspace.</blockquote>
            </section>

            <section id="create-your-event" aria-labelledby="event-title">
              <p className="chatgpt-guide__step">04 / Create</p>
              <h2 id="event-title">Start with an event name.</h2>
              <p>Create a test event called <strong>Weekend Trail Test</strong>. A name is enough to begin. Add a description or an exact schedule when you’re ready.</p>
              <blockquote>Create a StreamBridge event called “Weekend Trail Test” with two cameras: “Start line” and “Finish line”.</blockquote>
              <p>Review any confirmation ChatGPT shows before it makes changes. The pilot allows two camera slots per event.</p>
            </section>

            <section id="prepare-your-cameras" aria-labelledby="cameras-title">
              <p className="chatgpt-guide__step">05 / Prepare</p>
              <h2 id="cameras-title">Give every camera a place.</h2>
              <p>In Event workspace, select a camera to update its name, public visibility, public audio, or fixed map pin. Choose the invitation action to open its protected StreamBridge panel, then share the invitation with the person on that camera.</p>
              <p>Refresh readiness to see the latest reported state. A camera slot can exist before anyone connects; check the actual picture in StreamBridge before calling it ready.</p>
            </section>

            <section id="next-steps" aria-labelledby="next-title">
              <p className="chatgpt-guide__step">06 / Take it live</p>
              <h2 id="next-title">Your crew brings the pictures.</h2>
              <p>Open <strong>OBS setup</strong> when you’re ready to mix your cameras. StreamBridge opens a protected page with the camera scene collection. If you want your finished show to return to the event, choose <strong>Add live show</strong> first; the download then includes the OBS streaming profile too.</p>
              <p>Connect the cameras, import your OBS setup, and check the real video and public event page. ChatGPT prepares the event; camera capture and OBS run on your devices.</p>
              <Collapsible trigger="Something isn’t connecting?" keepMounted>
                <ul>
                  <li><strong>No custom plugin option:</strong> check access with your ChatGPT workspace administrator.</li>
                  <li><strong>Server unavailable:</strong> ask the StreamBridge team for the current pilot URL. A temporary tunnel only works while it is running.</li>
                  <li><strong>No permission to edit:</strong> confirm your StreamBridge role and the organization selected during linking.</li>
                  <li><strong>An edit conflicts:</strong> refresh the event, review the current details, then apply your change again.</li>
                  <li><strong>A save is uncertain:</strong> keep the panel open and use its retry action. If you closed it, inspect the event before creating anything again.</li>
                </ul>
              </Collapsible>
              <div className="chatgpt-guide__support"><h3>Need a hand?</h3><p>Our crew can help you connect and rehearse your first event.</p><DiscordSupportLink /></div>
            </section>
          </article>
        </div>
      </main>

      <SiteFooter />
    </div>
  )
}
